"use client";

import { useMemo } from "react";
import { useProcedure, useReducer } from "spacetimedb/react";
import { procedures, reducers } from "@/module_bindings";

/**
 * Reducer/procedure calls reject with a `SenderError` (a `spacetimedb`
 * `Error` subclass carrying the exact server-side message). We deliberately
 * never `import` from bare `"spacetimedb"` here — only `"spacetimedb/react"`
 * — because the bare package fails to resolve under jsdom's "browser"
 * export condition (see src/lib/stdb-map.ts's header comment for the full
 * story). So detection is via plain `instanceof Error` (SenderError IS an
 * Error) rather than a class import, and every wrapped call re-throws a
 * plain `Error(message)` so the existing `getApiErrorDescription` /
 * `showApiErrorToast` conventions (src/services/api-client.ts,
 * src/lib/api-error-toast.ts) work completely unchanged.
 */
function wrapErrors<Args extends unknown[], R>(
  call: (...args: Args) => Promise<R>,
): (...args: Args) => Promise<R> {
  return async (...args: Args) => {
    try {
      return await call(...args);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    }
  };
}

const API_KEY_RANDOM_BYTES = 32;

/**
 * Client-generated API key: `sk_<stage>_<64 lowercase hex>`. The
 * `create_api_key` reducer validates shape + uniqueness only — it never
 * generates the key itself (see `API_KEY_FORMAT` in
 * packages/spacetime/src/reducers/app.ts) — matching the legacy
 * `src/app/api/api-keys/route.ts` generation, just via the browser's CSPRNG
 * (`crypto.getRandomValues`) instead of node's `crypto.randomBytes`, since
 * this now runs client-side.
 */
export function generateApiKey(): string {
  const stage = process.env.NEXT_PUBLIC_APP_STAGE;
  if (!stage) {
    throw new Error(
      "NEXT_PUBLIC_APP_STAGE is not set — cannot generate an API key",
    );
  }
  const bytes = new Uint8Array(API_KEY_RANDOM_BYTES);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return `sk_${stage}_${hex}`;
}

/**
 * Typed wrappers over every Part A app reducer + the `invite_details`
 * procedure. This is the only place pages call into SpacetimeDB mutations —
 * `useReducer`/`useProcedure` (from `spacetimedb/react`) queue calls made
 * before the connection exists, so these are safe to invoke immediately on
 * mount.
 */
export function usePortalReducers() {
  const updateUser = useReducer(reducers.updateUser);
  const setLastSelectedOrganization = useReducer(
    reducers.setLastSelectedOrganization,
  );
  const createOrganization = useReducer(reducers.createOrganization);
  const updateOrganization = useReducer(reducers.updateOrganization);
  const deleteOrganization = useReducer(reducers.deleteOrganization);
  const inviteMember = useReducer(reducers.inviteMember);
  const acceptInvite = useReducer(reducers.acceptInvite);
  const declineInvite = useReducer(reducers.declineInvite);
  const createJoinRequest = useReducer(reducers.createJoinRequest);
  const updateMember = useReducer(reducers.updateMember);
  const removeMember = useReducer(reducers.removeMember);
  const createApiKey = useReducer(reducers.createApiKey);
  const deleteApiKey = useReducer(reducers.deleteApiKey);
  const createGlaciTag = useReducer(reducers.createGlaciTag);
  const updateGlaciTag = useReducer(reducers.updateGlaciTag);
  const deleteGlaciTag = useReducer(reducers.deleteGlaciTag);
  const createPlaybook = useReducer(reducers.createPlaybook);
  const updatePlaybook = useReducer(reducers.updatePlaybook);
  const deletePlaybook = useReducer(reducers.deletePlaybook);
  const inviteDetails = useProcedure(procedures.inviteDetails);

  return useMemo(
    () => ({
      updateUser: wrapErrors(updateUser),
      setLastSelectedOrganization: wrapErrors(setLastSelectedOrganization),
      createOrganization: wrapErrors(createOrganization),
      updateOrganization: wrapErrors(updateOrganization),
      deleteOrganization: wrapErrors(deleteOrganization),
      inviteMember: wrapErrors(inviteMember),
      acceptInvite: wrapErrors(acceptInvite),
      declineInvite: wrapErrors(declineInvite),
      createJoinRequest: wrapErrors(createJoinRequest),
      updateMember: wrapErrors(updateMember),
      removeMember: wrapErrors(removeMember),
      createApiKey: wrapErrors(createApiKey),
      deleteApiKey: wrapErrors(deleteApiKey),
      createGlaciTag: wrapErrors(createGlaciTag),
      updateGlaciTag: wrapErrors(updateGlaciTag),
      deleteGlaciTag: wrapErrors(deleteGlaciTag),
      createPlaybook: wrapErrors(createPlaybook),
      updatePlaybook: wrapErrors(updatePlaybook),
      deletePlaybook: wrapErrors(deletePlaybook),
      inviteDetails: wrapErrors(inviteDetails),
    }),
    [
      updateUser,
      setLastSelectedOrganization,
      createOrganization,
      updateOrganization,
      deleteOrganization,
      inviteMember,
      acceptInvite,
      declineInvite,
      createJoinRequest,
      updateMember,
      removeMember,
      createApiKey,
      deleteApiKey,
      createGlaciTag,
      updateGlaciTag,
      deleteGlaciTag,
      createPlaybook,
      updatePlaybook,
      deletePlaybook,
      inviteDetails,
    ],
  );
}
